import type { PlcNode, PlcPou } from './plcApi'

/**
 * Draws the ladder diagram from the parsed graph, using the element
 * coordinates the PLCopen XML itself carries — no layout algorithm, the
 * drawing is placed exactly where the program's author drew it. Edges are
 * rendered as elbow connectors between element anchors (the XML's own
 * routing waypoints are not preserved by the parser, so connectors are
 * approximate; symbols and positions are exact).
 */

const DEFAULT_W = 24
const DEFAULT_H = 20
const RAIL_H = 100

const w = (n: PlcNode) => n.width ?? DEFAULT_W
const h = (n: PlcNode) => n.height ?? DEFAULT_H

const isRail = (n: PlcNode) => n.kind === 'leftPowerRail' || n.kind === 'rightPowerRail'

const LadderView = ({ pou }: { pou: PlcPou }) => {
  const placed = pou.nodes.filter((n) => n.x !== null && n.y !== null)
  if (pou.language !== 'LD' || placed.length === 0) return null

  const byId = new Map(pou.nodes.map((n) => [n.local_id, n]))
  const minX = Math.min(...placed.map((n) => n.x as number)) - 30
  const maxX = Math.max(...placed.map((n) => (n.x as number) + w(n))) + 40
  const minY = Math.min(...placed.map((n) => n.y as number)) - 26
  const maxY = Math.max(...placed.map((n) => (n.y as number) + (isRail(n) ? (n.height ?? RAIL_H) : h(n)))) + 20

  const edgePath = (source: PlcNode, target: PlcNode): string | null => {
    if (source.x === null || source.y === null || target.x === null || target.y === null) return null
    let sx = source.x + w(source)
    let sy = source.y + h(source) / 2
    let tx = target.x
    let ty = target.y + h(target) / 2
    // Rails span many rungs: attach at the counterpart's height, not the rail center.
    if (isRail(source)) {
      sx = source.x + (source.width ?? 10)
      sy = ty
    }
    if (isRail(target)) {
      tx = target.x
      ty = sy
    }
    const bend = tx - 8
    return `M ${sx} ${sy} L ${bend} ${sy} L ${bend} ${ty} L ${tx} ${ty}`
  }

  return (
    <svg
      data-testid="plc-ladder-svg"
      viewBox={`${minX} ${minY} ${maxX - minX} ${maxY - minY}`}
      className="w-full text-foreground"
      style={{ maxHeight: '24rem' }}
      role="img"
      aria-label={`Ladder diagram for ${pou.name}`}
    >
      {pou.edges.map((edge, i) => {
        const source = byId.get(edge.source)
        const target = byId.get(edge.target)
        if (!source || !target) return null
        const d = edgePath(source, target)
        if (!d) return null
        return (
          <path
            key={`e${i}`}
            d={d}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.2}
            opacity={0.75}
          />
        )
      })}

      {placed.map((n) => {
        const x = n.x as number
        const y = n.y as number
        const label = (
          <text
            x={x + w(n) / 2}
            y={y - 6}
            textAnchor="middle"
            fontSize={9}
            fontFamily="ui-monospace, monospace"
            fill="currentColor"
          >
            {n.negated && n.kind === 'contact' ? `${n.label} (NC)` : n.label}
          </text>
        )

        switch (n.kind) {
          case 'leftPowerRail':
          case 'rightPowerRail': {
            const railX = x + (n.width ?? 10) / 2
            return (
              <line
                key={n.local_id}
                x1={railX}
                y1={y}
                x2={railX}
                y2={y + (n.height ?? RAIL_H)}
                stroke="currentColor"
                strokeWidth={2.5}
              />
            )
          }
          case 'contact':
            return (
              <g key={n.local_id}>
                <line x1={x} y1={y} x2={x} y2={y + h(n)} stroke="currentColor" strokeWidth={1.8} />
                <line
                  x1={x + w(n)}
                  y1={y}
                  x2={x + w(n)}
                  y2={y + h(n)}
                  stroke="currentColor"
                  strokeWidth={1.8}
                />
                {n.negated && (
                  <line
                    x1={x - 3}
                    y1={y + h(n) + 3}
                    x2={x + w(n) + 3}
                    y2={y - 3}
                    stroke="currentColor"
                    strokeWidth={1.4}
                  />
                )}
                {label}
              </g>
            )
          case 'coil':
            return (
              <g key={n.local_id}>
                <circle
                  cx={x + w(n) / 2}
                  cy={y + h(n) / 2}
                  r={h(n) / 2}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.8}
                />
                {label}
              </g>
            )
          case 'block':
            return (
              <g key={n.local_id}>
                <rect
                  x={x}
                  y={y}
                  width={n.width ?? 60}
                  height={n.height ?? 40}
                  rx={3}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.4}
                />
                <text
                  x={x + (n.width ?? 60) / 2}
                  y={y + (n.height ?? 40) / 2 + 3}
                  textAnchor="middle"
                  fontSize={9}
                  fontFamily="ui-monospace, monospace"
                  fill="currentColor"
                >
                  {n.label}
                </text>
              </g>
            )
          case 'inVariable':
          case 'outVariable':
            return (
              <text
                key={n.local_id}
                x={x + w(n) / 2}
                y={y + h(n) / 2 + 3}
                textAnchor="middle"
                fontSize={9}
                fontFamily="ui-monospace, monospace"
                fill="currentColor"
              >
                {n.label}
              </text>
            )
          default:
            return null
        }
      })}
    </svg>
  )
}

export default LadderView
